// src/pages/builder/LabelItPanel.js
//
// Layout Builder ▸ 🏷️ Label It! games — make "label the picture" games.
//
// For a topic:
//   1. PICTURE   make one with AI right here (Image studio), upload one, or
//                paste a link. It's saved as a WebP (long side 1600px) in
//                topic-images/games/{topic}/.
//   2. TILES     type each name (and/or give it a small picture). EVERY TILE
//                HAS ITS OWN DROP SPOT on the picture: click the picture to
//                add a tile right there, or drag a numbered marker to move a
//                tile's spot.
//   3. MORE PICTURES (optional) — "+ Add picture" makes a second board, a
//                third…; students play them in order, like levels.
//   4. ▶ Try it, Save draft, Publish. Publish makes it the topic's 🎮 Game
//                (the old game is backed up first, as in the Game generator).
//
// Reads topicLibrary, quizzes, labelGames. Writes labelGames/{topic} (Save)
// and, on Publish only, quizzes/{topic} + quizBackups. Never touches a page
// (portalLayouts) or another topic.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { exactKey, getAllLibraryTopics, makeToolKey, SUBJECT_LABELS } from "../../services/topicLibraryService";
import { getLiveGames } from "../../services/gameGeneratorService";
import { BONUS_OPTIONS } from "../../services/gameGenerator";
import {
  BOARD_ASPECTS,
  GAME_PIC_LOOKS,
  GAME_PIC_QUALITIES,
  makeBoardPicture,
  makeGamePicture,
  uploadBoardPicture,
  uploadGamePicture,
} from "../../services/gamePictureService";
import { cleanBoards, getLabelDrafts, labelProblems, publishLabelGame, saveLabelDraft } from "../../services/labelItService";
import LabelItGame from "../../components/quizzes/LabelItGame";
import { PreviewFrame } from "./GameLibraryPanel";
import "./labelItPanel.css";

const COLORS = ["#f59e0b", "#ec4899", "#3b82f6", "#22c55e", "#a855f7", "#14b8a6"];
const clone = (x) => JSON.parse(JSON.stringify(x));
const newId = () => `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const newBoard = () => ({ question: "Drag each name to its place on the picture!", image: "", imageAlt: "", tiles: [] });
// where "+ Add tile" puts a new spot before you drag it: a loose grid
const spotFor = (n) => ({ x: 14 + (n % 5) * 18, y: 16 + (Math.floor(n / 5) % 4) * 22 });
const BONUS_CHOICES = BONUS_OPTIONS.filter((b) => b.value !== "typingQuiz");

/* ------------------------------------------------------------------ */
/* Picture source: AI / upload / link                                  */
/* ------------------------------------------------------------------ */

const PictureMaker = ({ topic, hasPicture, onPicture, onCancel, defaultIdea = "" }) => {
  const [idea, setIdea] = useState(defaultIdea);
  const [aspect, setAspect] = useState("4:3");
  const [look, setLook] = useState("sbl");
  const [quality, setQuality] = useState("standard");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const run = async (kind, job) => {
    setBusy(kind);
    setError("");
    try {
      const { url } = await job();
      if (url) onPicture(url);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="lbl-maker">
      <div className="lbl-maker-box">
        <h4>🎨 Make it with AI</h4>
        <textarea
          rows={3}
          value={idea}
          placeholder={`What to draw — e.g. "a map of the world showing all 7 continents and the 5 oceans"`}
          onChange={(e) => setIdea(e.target.value)}
        />
        <div className="lbl-row">
          <label>
            Shape
            <select value={aspect} onChange={(e) => setAspect(e.target.value)}>
              {BOARD_ASPECTS.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Look
            <select value={look} onChange={(e) => setLook(e.target.value)}>
              {GAME_PIC_LOOKS.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Quality
            <select value={quality} onChange={(e) => setQuality(e.target.value)}>
              {GAME_PIC_QUALITIES.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="lb-btn lb-btn-sm"
            disabled={!!busy || !idea.trim()}
            onClick={() => run("ai", () => makeBoardPicture({ topic, idea, aspect, look, quality }))}
          >
            {busy === "ai" ? "Making… (up to a minute)" : "Make picture"}
          </button>
        </div>
        <p className="lbl-muted">The picture is made with no words on it — naming the parts is the game.</p>
      </div>

      <div className="lbl-maker-box">
        <h4>📁 Upload or 🔗 link</h4>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) run("up", () => uploadBoardPicture({ topic, file }));
          }}
        />
        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" disabled={!!busy} onClick={() => fileRef.current?.click()}>
          {busy === "up" ? "Uploading…" : "Choose a picture file"}
        </button>
        <div className="lbl-row">
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://… a picture already online" />
          <button
            type="button"
            className="lb-btn lb-btn-ghost lb-btn-sm"
            disabled={!!busy || !/^https:\/\/\S+$/i.test(link.trim())}
            onClick={() => onPicture(link.trim())}
          >
            Use link
          </button>
        </div>
        {hasPicture && (
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={onCancel}>
            Keep the current picture
          </button>
        )}
      </div>
      {error && <p className="lbl-error">{error}</p>}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* The picture with its spots: click to add, drag to move              */
/* ------------------------------------------------------------------ */

const Placer = ({ board, selected, onSelect, onMove, onAddAt }) => {
  const ref = useRef(null);
  const dragRef = useRef(null); // { index, moved }
  // pressing a marker ends in a click on the picture itself (pointer capture),
  // which must not add a new tile
  const skipClick = useRef(false);

  const pctAt = (e) => {
    const r = ref.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)),
    };
  };

  return (
    <div
      className="lbl-placer"
      ref={ref}
      onPointerMove={(e) => {
        const d = dragRef.current;
        if (!d) return;
        d.moved = true;
        onMove(d.index, pctAt(e));
      }}
      onPointerUp={() => {
        if (dragRef.current) skipClick.current = true;
        dragRef.current = null;
      }}
      onClick={(e) => {
        if (skipClick.current) {
          skipClick.current = false;
          return;
        }
        if (e.target.closest?.("[data-marker]")) return;
        onAddAt(pctAt(e));
      }}
    >
      <img src={board.image} alt={board.imageAlt || ""} draggable="false" />
      {board.tiles.map((t, i) => (
        <button
          key={t.id}
          type="button"
          data-marker
          className={`lbl-marker ${selected === i ? "is-on" : ""}`}
          style={{ left: `${t.x}%`, top: `${t.y}%`, "--c": COLORS[i % COLORS.length] }}
          title={`${i + 1}. ${t.text || "(no words)"} — drag to move`}
          onPointerDown={(e) => {
            e.stopPropagation();
            dragRef.current = { index: i, moved: false };
            onSelect(i);
            try {
              ref.current.setPointerCapture(e.pointerId);
            } catch {
              /* ignore */
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <b>{i + 1}</b>
          {t.text && <span>{t.text}</span>}
        </button>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* One tile row                                                        */
/* ------------------------------------------------------------------ */

const TileRow = ({ topic, tile, index, selected, onSelect, onChange, onRemove, inputRef }) => {
  const [picOpen, setPicOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const run = async (kind, job) => {
    setBusy(kind);
    setError("");
    try {
      const { url } = await job();
      if (url) onChange({ image: url });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className={`lbl-tile ${selected ? "is-on" : ""}`} onClick={onSelect}>
      <span className="lbl-num" style={{ "--c": COLORS[index % COLORS.length] }}>
        {index + 1}
      </span>
      <input ref={inputRef} value={tile.text || ""} placeholder="Name on the tile, e.g. Africa" onChange={(e) => onChange({ text: e.target.value })} />
      <button type="button" className={`lbl-pic ${tile.image ? "has-pic" : ""}`} title="Picture on this tile (optional)" onClick={() => setPicOpen((v) => !v)}>
        {tile.image ? <img src={tile.image} alt="" /> : "＋🖼"}
      </button>
      <button type="button" className="lbl-x" title="Remove this tile and its spot" onClick={onRemove}>
        ✕
      </button>
      {picOpen && (
        <div className="lbl-tile-pic" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="lb-btn lb-btn-sm"
            disabled={!!busy || !String(tile.text || "").trim()}
            onClick={() => run("ai", () => makeGamePicture({ topic, word: tile.text }))}
          >
            {busy === "ai" ? "Making…" : `🎨 AI picture of “${tile.text || "…"}”`}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) run("up", () => uploadGamePicture({ topic, file, word: tile.text }));
            }}
          />
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" disabled={!!busy} onClick={() => fileRef.current?.click()}>
            {busy === "up" ? "Uploading…" : "📁 Upload"}
          </button>
          {tile.image && (
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => onChange({ image: "" })}>
              Remove picture
            </button>
          )}
          {error && <span className="lbl-error">{error}</span>}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Editor for one topic                                                */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Pictures + tiles + spots — shared by this panel and the Game        */
/* generator's "Label It!" game type                                   */
/* ------------------------------------------------------------------ */

/**
 * boards / onChange(updater or value): the board list.
 * defaultIdea: what the AI picture box starts with (the generator fills it
 * from the song's words).
 */
export const BoardsEditor = ({ topic, boards, onChange, defaultIdea = "" }) => {
  const setBoards = onChange;
  const [bi, setBi] = useState(0);
  const [sel, setSel] = useState(null); // selected tile index on this board
  const [changing, setChanging] = useState(false); // picture maker open over an existing picture
  const inputRefs = useRef({});
  const focusNext = useRef(null);
  const board = boards[bi] || boards[0] || newBoard();

  useEffect(() => {
    if (focusNext.current !== null) {
      inputRefs.current[focusNext.current]?.focus();
      focusNext.current = null;
    }
  });

  const setBoard = (patch) => {
    setBoards((all) => all.map((b, i) => (i === bi ? { ...b, ...(typeof patch === "function" ? patch(b) : patch) } : b)));
  };
  const setTile = (ti, patch) => setBoard((b) => ({ tiles: b.tiles.map((t, j) => (j === ti ? { ...t, ...patch } : t)) }));
  const addTile = (at) => {
    const n = board.tiles.length;
    const spot = at || spotFor(n);
    setBoard((b) => ({ tiles: [...b.tiles, { id: newId(), text: "", image: "", x: Math.round(spot.x * 10) / 10, y: Math.round(spot.y * 10) / 10 }] }));
    setSel(n);
    focusNext.current = n;
  };
  const removeTile = (ti) => {
    setBoard((b) => ({ tiles: b.tiles.filter((_, j) => j !== ti) }));
    setSel(null);
  };

  const addBoard = () => {
    setBoards((all) => [...all, newBoard()]);
    setBi(boards.length);
    setSel(null);
  };
  const removeBoard = () => {
    if (boards.length < 2) return;
    if (!window.confirm(`Delete picture ${bi + 1} and its ${board.tiles.length} tiles?`)) return;
    setBoards((all) => all.filter((_, i) => i !== bi));
    setBi(Math.max(0, bi - 1));
    setSel(null);
  };
  const moveBoard = (d) => {
    const j = bi + d;
    if (j < 0 || j >= boards.length) return;
    setBoards((all) => {
      const next = [...all];
      [next[bi], next[j]] = [next[j], next[bi]];
      return next;
    });
    setBi(j);
  };

  const showMaker = !board.image || changing;

  return (
    <>
      {/* ---------- pictures (levels) ---------- */}
      <div className="lbl-boards">
        {boards.map((b, i) => (
          <button
            key={i}
            type="button"
            className={`lbl-board-tab ${i === bi ? "is-on" : ""}`}
            onClick={() => {
              setBi(i);
              setSel(null);
              setChanging(false);
            }}
          >
            {b.image ? <img src={b.image} alt="" /> : <span>🖼</span>}
            Picture {i + 1}
            <small>{b.tiles.length} tiles</small>
          </button>
        ))}
        <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" onClick={addBoard} title="Another picture students play after this one">
          + Add picture
        </button>
        {boards.length > 1 && (
          <span className="lbl-board-tools">
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => moveBoard(-1)} disabled={bi === 0}>
              ← Move
            </button>
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => moveBoard(1)} disabled={bi === boards.length - 1}>
              Move →
            </button>
            <button type="button" className="lb-btn lb-btn-danger lb-btn-sm" onClick={removeBoard}>
              Delete picture {bi + 1}
            </button>
          </span>
        )}
      </div>

      <label className="lbl-field">
        What students are asked
        <input value={board.question} onChange={(e) => setBoard({ question: e.target.value })} placeholder="e.g. Label the continents and oceans!" />
      </label>

      {showMaker ? (
        <PictureMaker
          key={`maker-${bi}`}
          topic={topic}
          defaultIdea={defaultIdea}
          hasPicture={Boolean(board.image)}
          onPicture={(url) => {
            setBoard({ image: url });
            setChanging(false);
          }}
          onCancel={() => setChanging(false)}
        />
      ) : (
        <div className="lbl-work">
          <div className="lbl-work-pic">
            <Placer
              board={board}
              selected={sel}
              onSelect={setSel}
              onMove={(ti, p) => setTile(ti, { x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 })}
              onAddAt={(p) => addTile(p)}
            />
            <div className="lbl-row">
              <span className="lbl-muted">
                <b>Click the picture</b> to add a tile with its spot right there. <b>Drag</b> a numbered marker to move a spot.
              </span>
              <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setChanging(true)}>
                Change picture
              </button>
            </div>
            <label className="lbl-field">
              What the picture shows (read to students who can't see it)
              <input value={board.imageAlt} onChange={(e) => setBoard({ imageAlt: e.target.value })} placeholder="e.g. A map of the world" />
            </label>
          </div>

          <div className="lbl-work-tiles">
            <h4>Tiles ({board.tiles.length})</h4>
            {board.tiles.length === 0 && <p className="lbl-muted">No tiles yet — click the picture where the first name goes.</p>}
            {board.tiles.map((t, i) => (
              <TileRow
                key={t.id}
                topic={topic}
                tile={t}
                index={i}
                selected={sel === i}
                onSelect={() => setSel(i)}
                onChange={(patch) => setTile(i, patch)}
                onRemove={() => removeTile(i)}
                inputRef={(el) => {
                  inputRefs.current[i] = el;
                }}
              />
            ))}
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => addTile()}>
              + Add tile
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const Editor = ({ topic, toolKey, draft, live, onClose, onSaved }) => {
  const [boards, setBoardsRaw] = useState(() => (draft?.boards?.length ? clone(draft.boards) : [newBoard()]));
  const [bonus, setBonus] = useState(draft?.bonusRoundType || "diamondTap");
  const [dirty, setDirty] = useState(!draft);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);
  const [trying, setTrying] = useState(false);
  const [tryResult, setTryResult] = useState(null);
  const setBoards = (u) => {
    setBoardsRaw(u);
    setDirty(true);
  };
  const problems = labelProblems(boards);

  const save = async () => {
    setBusy("save");
    try {
      const saved = await saveLabelDraft(toolKey, { title: topic.title, boards, bonusRoundType: bonus, status: "draft" });
      setDirty(false);
      onSaved(toolKey, { ...saved, id: toolKey });
      setNotice({ tone: "ok", text: "Draft saved. Students don’t see it until you publish." });
    } catch (e) {
      setNotice({ tone: "error", text: `Could not save: ${e?.message || e}` });
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    if (problems.length) return;
    const replacing = live
      ? `This topic's live game (${live.gameType}, ${live.count} ${live.count === 1 ? "question" : "questions"}) is backed up and then replaced.\n\n`
      : "";
    if (!window.confirm(`${replacing}Publish this Label It! game (${boards.length} picture${boards.length === 1 ? "" : "s"}) for “${topic.title}” to students?`)) return;
    setBusy("publish");
    try {
      const res = await publishLabelGame({ slug: toolKey, title: topic.title, boards, bonusRoundType: bonus });
      setDirty(false);
      onSaved(toolKey, { id: toolKey, boards: cleanBoards(boards), bonusRoundType: bonus, status: "published" }, { gameType: "labelIt", count: res.count });
      setNotice({ tone: "ok", text: `Published! “${topic.title}” now plays Label It! (${res.count} picture${res.count === 1 ? "" : "s"}).${res.backupId ? " The old game was backed up." : ""}` });
    } catch (e) {
      setNotice({ tone: "error", text: `Could not publish: ${e?.message || e}` });
    } finally {
      setBusy("");
    }
  };


  return (
    <div className="lbl-editor">
      <div className="lbl-editor-head">
        <button
          type="button"
          className="lb-btn lb-btn-ghost lb-btn-sm"
          onClick={() => {
            if (dirty && !window.confirm("Leave without saving?")) return;
            onClose();
          }}
        >
          ← All topics
        </button>
        <div className="lbl-editor-title">
          <h3>{topic.title || toolKey}</h3>
          <span className="lbl-muted">
            {toolKey}
            {live ? ` · live game now: ${live.gameType}` : " · no live game yet"}
          </span>
        </div>
        {dirty && <span className="lbl-pill is-amber">Not saved</span>}
      </div>

      {notice && (
        <div className={`lbl-notice is-${notice.tone}`}>
          {notice.text}
          <button type="button" className="lbl-x" onClick={() => setNotice(null)}>
            ✕
          </button>
        </div>
      )}

      <BoardsEditor topic={topic} boards={boards} onChange={setBoards} />

      {/* ---------- try / save / publish ---------- */}
      {trying && (
        <div className="lbl-try">
          <div className="lbl-try-bar">
            <strong>▶ Trying it — nothing is recorded</strong>
            {tryResult && (
              <span>
                Score {tryResult.score} · {tryResult.answers.filter((a) => a.correct).length} of {tryResult.answers.length} right
              </span>
            )}
            <button
              type="button"
              className="lb-btn lb-btn-ghost lb-btn-sm"
              onClick={() => {
                setTrying(false);
                setTryResult(null);
              }}
            >
              Close
            </button>
          </div>
          <div className="lbl-try-stage">
            {tryResult ? (
              <div className="lbl-try-done">
                <p>
                  Finished! The bonus round ({BONUS_CHOICES.find((b) => b.value === bonus)?.label || bonus}) plays next for students.
                </p>
                <button type="button" className="lb-btn lb-btn-sm" onClick={() => setTryResult(null)}>
                  ↻ Play again
                </button>
              </div>
            ) : (
              <PreviewFrame>
                <LabelItGame questions={cleanBoards(boards)} topic={{ title: topic.title }} onComplete={(r) => setTryResult(r)} />
              </PreviewFrame>
            )}
          </div>
        </div>
      )}

      {problems.length > 0 && (
        <div className="lbl-notice is-error">
          <strong>Before publishing:</strong>
          <ul>
            {problems.slice(0, 6).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="lbl-savebar">
        <button
          type="button"
          className="lb-btn lb-btn-soft"
          disabled={problems.length > 0}
          onClick={() => {
            setTryResult(null);
            setTrying((v) => !v);
          }}
        >
          {trying ? "Hide game" : "▶ Try it"}
        </button>
        <label className="lbl-inline">
          Bonus round
          <select
            value={bonus}
            onChange={(e) => {
              setBonus(e.target.value);
              setDirty(true);
            }}
          >
            {BONUS_CHOICES.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
        </label>
        <span className="lbl-spacer" />
        <button type="button" className="lb-btn lb-btn-ghost" onClick={save} disabled={!!busy}>
          {busy === "save" ? "Saving…" : "Save draft"}
        </button>
        <button type="button" className="lb-btn" onClick={publish} disabled={!!busy || problems.length > 0}>
          {busy === "publish" ? "Publishing…" : "🚀 Publish to students"}
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const LabelItPanel = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topics, setTopics] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [live, setLive] = useState({});
  const [search, setSearch] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [active, setActive] = useState(null); // tool key
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, d, l] = await Promise.all([
        getAllLibraryTopics(),
        getLabelDrafts().catch((e) => {
          if (e?.code === "permission-denied") throw e;
          return {};
        }),
        getLiveGames().catch(() => ({})),
      ]);
      setTopics(t);
      setDrafts(d);
      setLive(l);
    } catch (e) {
      setError(
        e?.code === "permission-denied"
          ? "Permission denied reading labelGames. Deploy the updated firestore.rules (it adds the labelGames block)."
          : `Could not load: ${e?.message || e}`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !topics.length && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toolKey = useMemo(() => makeToolKey(topics), [topics]);
  const rows = useMemo(
    () =>
      topics
        .map((t) => {
          const key = toolKey(t);
          return { key, topic: { ...t, slug: key }, draft: drafts[key] || null, live: live[key] || live[exactKey(t.slug || t.id)] || null };
        })
        .filter((r) => r.key),
    [topics, drafts, live, toolKey]
  );
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => !onlyMine || r.draft || r.live?.gameType === "labelIt")
      .filter((r) => !q || String(r.topic.title || "").toLowerCase().includes(q) || r.key.includes(q))
      .slice(0, 80);
  }, [rows, search, onlyMine]);
  const current = active ? rows.find((r) => r.key === active) : null;

  return (
    <section className="lb-card lbl-panel" ref={panelRef}>
      <div className="lb-card-head">
        <span className="lb-step lbl-step">🏷️</span>
        <div>
          <h2>Label It! games</h2>
          <p className="lb-hint">
            Put a picture up (make it with AI, upload or link one), add name tiles, and put each tile&apos;s spot on the picture. Students drag the tiles to the
            right spots and press “I&apos;m finished”. Add more pictures to make levels.
          </p>
        </div>
        <div className="lbl-head-actions">
          {open && !current && (
            <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && error && <div className="lbl-notice is-error">{error}</div>}

      {open &&
        (current ? (
          <Editor
            key={current.key}
            topic={current.topic}
            toolKey={current.key}
            draft={current.draft}
            live={current.live}
            onClose={() => setActive(null)}
            onSaved={(key, draft, liveEntry) => {
              setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...draft } }));
              if (liveEntry) setLive((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...liveEntry } }));
            }}
          />
        ) : (
          <>
            <div className="lbl-row lbl-list-controls">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a topic…" />
              <label className="lbl-inline">
                <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} /> Only topics with a Label It! game
              </label>
            </div>
            {loading && !topics.length ? (
              <p className="lbl-muted">Loading topics…</p>
            ) : (
              <div className="lbl-table-wrap">
                <table className="lbl-table">
                  <thead>
                    <tr>
                      <th>Topic</th>
                      <th>Subject</th>
                      <th>Label It!</th>
                      <th>Live game now</th>
                      <th aria-label="Open" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => (
                      <tr key={r.key}>
                        <td>
                          <b>{r.topic.title || r.key}</b> <small>{r.key}</small>
                        </td>
                        <td>{SUBJECT_LABELS[r.topic.subject] || r.topic.subject || "—"}</td>
                        <td>
                          {r.draft ? (
                            <span className={`lbl-pill ${r.draft.status === "published" ? "is-green" : "is-amber"}`}>
                              {r.draft.status === "published" ? "Published" : "Draft"} · {(r.draft.boards || []).length} picture{(r.draft.boards || []).length === 1 ? "" : "s"}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{r.live ? `${r.live.gameType}${r.live.gameType === "labelIt" ? " ✓" : ""}` : "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="lb-btn lb-btn-sm"
                            onClick={() => {
                              setActive(r.key);
                              setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
                            }}
                          >
                            {r.draft ? "Open" : "Make one"}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!shown.length && (
                      <tr>
                        <td colSpan={5}>No topics match.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ))}
    </section>
  );
};

export default LabelItPanel;
